import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { QuestionCircleFilled } from "@ant-design/icons";
import { MessageDescriptor } from "@formatjs/intl/src/types";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faAsterisk } from "@fortawesome/pro-solid-svg-icons/faAsterisk";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Badge from "antd/es/badge";
import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity/types";
import { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import { Button, useModal } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import useFirstLastNameRequirement from "@app/util/hook/entityForm/useFirstLastNameRequirement";

import {
  getActiveEntityType,
  isEditingActivityEmailVariantLayout,
  isEditingActivityPrimaryLayout,
} from "../../store";

import PersonNameRequirementOption from "./enum/PersonNameRequirementOption";
import messages from "./messages";
import styles from "./Requirement.module.scss";
import getPersonNameRequiredOptionByValues from "./util/getPersonNamerequiredOptionByValues";
import getValuesByPersonNameRequiredOption from "./util/getValuesByPersonNameRequiredOption";

interface Props {
  activeSchema: LayoutSchemaField[];
  activityEmailVariantLayout: boolean;
  activityPrimaryLayout: boolean;
  analyticsIssuer?: AnalyticsService;
  entityType: EntityTypesSupportingFieldCustomization;
  isCalculated: boolean;
  isSystemRequired: boolean;
  isUnique: boolean;
  onUpdateSchema: (updatedSchema: LayoutSchemaField[]) => void;
  row: LayoutSchemaField;
}

const Requirement: React.FC<Props> = ({
  activeSchema,
  activityEmailVariantLayout,
  activityPrimaryLayout,
  analyticsIssuer,
  entityType,
  isCalculated,
  isSystemRequired,
  isUnique,
  onUpdateSchema,
  row,
}) => {
  const intl = useIntl();
  const [modal, contextHolder] = useModal();

  const [isFirstNameRequired, isLastNameRequired]: [boolean, boolean] =
    useFirstLastNameRequirement(activeSchema);

  const handleToggleRequired = useCallback(() => {
    const currentRow = activeSchema.find(
      (schemaRow: LayoutSchemaField) =>
        schemaRow.field === row.field && schemaRow.displayOrder === row.displayOrder
    );

    analyticsIssuer?.clicked([currentRow?.required ? "Mark Not Required" : "Mark Required"]);

    let newRequiredValue = false;
    const updatedSchema = activeSchema.map((schemaRow: LayoutSchemaField) => {
      if (schemaRow.field === row.field) {
        newRequiredValue = !schemaRow.required;
        return { ...schemaRow, required: !schemaRow.required };
      }
      return schemaRow;
    });

    const activityField =
      entityType === EntityType.ACTIVITY
        ? activityFieldModel.getByPlatformName(row.field)
        : undefined;

    const activityRelationshipField = activityField?.hasFeature(FieldFeature.RELATIONSHIPS);

    if (
      newRequiredValue &&
      activityRelationshipField &&
      (activityPrimaryLayout || activityEmailVariantLayout)
    ) {
      const showActivityRequiredRelationshipAlert = ({
        emailVariantLayoutDescription,
        primaryLayoutDescription,
        subTitle,
      }: {
        emailVariantLayoutDescription: MessageDescriptor;
        primaryLayoutDescription: MessageDescriptor;
        subTitle: MessageDescriptor;
      }) => {
        modal.show({
          autoFocusButton: null,
          cancelButtonProps: {
            type: "primary",
          },
          cancelText: intl.formatMessage(messages.activityRequiredRelationshipAlertCancelButton),
          centered: true,
          className: styles.activityRequiredRelationshipModal,
          closable: true,
          content: (
            <>
              <div className={styles.activityRequiredRelationshipModalSubTitle}>
                {intl.formatMessage(subTitle)}
              </div>
              <div className={styles.activityRequiredRelationshipModalDescription}>
                {intl.formatMessage(
                  activityPrimaryLayout ? primaryLayoutDescription : emailVariantLayoutDescription
                )}
              </div>
            </>
          ),
          icon: <QuestionCircleFilled />,
          okButtonProps: { danger: true, type: "default" },
          okText: intl.formatMessage(messages.activityRequiredRelationshipAlertOkButton),
          onOk: () => onUpdateSchema(updatedSchema),
          title: intl.formatMessage(messages.activityRequiredRelationshipAlertTitle),
          type: "confirm",
          width: "30rem",
        });
      };

      switch (activityField?.name) {
        case ActivityFieldName.COMPANY: {
          showActivityRequiredRelationshipAlert({
            emailVariantLayoutDescription:
              messages.activityRequiredRelationshipAlertCompanyEmailVariantLayoutDescription,
            primaryLayoutDescription:
              messages.activityRequiredRelationshipAlertCompanyPrimaryLayoutDescription,
            subTitle: messages.activityRequiredRelationshipAlertCompanySubTitle,
          });
          break;
        }
        case ActivityFieldName.PERSON: {
          showActivityRequiredRelationshipAlert({
            emailVariantLayoutDescription:
              messages.activityRequiredRelationshipAlertPersonEmailVariantLayoutDescription,
            primaryLayoutDescription:
              messages.activityRequiredRelationshipAlertPersonPrimaryLayoutDescription,
            subTitle: messages.activityRequiredRelationshipAlertPersonSubTitle,
          });
          break;
        }
        case ActivityFieldName.DEAL: {
          showActivityRequiredRelationshipAlert({
            emailVariantLayoutDescription:
              messages.activityRequiredRelationshipAlertDealEmailVariantLayoutDescription,
            primaryLayoutDescription:
              messages.activityRequiredRelationshipAlertDealPrimaryLayoutDescription,
            subTitle: messages.activityRequiredRelationshipAlertDealSubTitle,
          });
          break;
        }
      }
    } else if (isUnique && !currentRow?.required) {
      modal.show({
        cancelText: intl.formatMessage(messages.cancelButton),
        centered: true,
        content: intl.formatMessage(messages.uniqueCustomFieldRequiredAlertDescription, {
          b: (text) => <b>{text}</b>,
        }),
        okText: intl.formatMessage(messages.continueButton),
        onOk: () => onUpdateSchema(updatedSchema),
        title: intl.formatMessage(messages.uniqueCustomFieldRequiredAlertTitle),
        type: "confirm",
      });
    } else {
      onUpdateSchema(updatedSchema);
    }
  }, [
    entityType,
    activeSchema,
    analyticsIssuer,
    activityPrimaryLayout,
    activityEmailVariantLayout,
    intl,
    isUnique,
    modal,
    onUpdateSchema,
    row,
  ]);

  const handleChangeFirstLastNameRequirements = useCallback(
    ({ key }) => {
      const [firstNameRequired, lastNameRequired] = getValuesByPersonNameRequiredOption(key);
      const updatedSchema = activeSchema.map((schemaRow: LayoutSchemaField) =>
        schemaRow.field === "firstName"
          ? { ...schemaRow, required: firstNameRequired }
          : schemaRow.field === "lastName"
          ? { ...schemaRow, required: lastNameRequired }
          : schemaRow
      );
      onUpdateSchema(updatedSchema);
    },
    [activeSchema, onUpdateSchema]
  );

  const getPersonOptionRow = useCallback(
    (name, checked) => (
      <Row gutter={layout.spacerM} justify="space-between">
        <Col>{name}</Col>
        {checked && (
          <Col>
            <FontAwesomeIcon icon={faCheck} />
          </Col>
        )}
      </Row>
    ),
    []
  );

  const personNameMenu = useMemo(() => {
    const activeKey = getPersonNameRequiredOptionByValues(isFirstNameRequired, isLastNameRequired);
    return {
      items: [
        {
          key: PersonNameRequirementOption.NONE_REQUIRED,
          label: getPersonOptionRow(
            intl.formatMessage(messages.personNameNoneRequired),
            activeKey === PersonNameRequirementOption.NONE_REQUIRED
          ),
        },
        {
          key: PersonNameRequirementOption.ALL_REQUIRED,
          label: getPersonOptionRow(
            intl.formatMessage(messages.personNameAllRequired),
            activeKey === PersonNameRequirementOption.ALL_REQUIRED
          ),
        },
        {
          key: PersonNameRequirementOption.FIRST_NAME_REQUIRED,
          label: getPersonOptionRow(
            intl.formatMessage(messages.personNameFirstNameRequired),
            activeKey === PersonNameRequirementOption.FIRST_NAME_REQUIRED
          ),
        },
        {
          key: PersonNameRequirementOption.LAST_NAME_REQUIRED,
          label: getPersonOptionRow(
            intl.formatMessage(messages.personNameLastNameRequired),
            activeKey === PersonNameRequirementOption.LAST_NAME_REQUIRED
          ),
        },
      ],
      onClick: handleChangeFirstLastNameRequirements,
      selectable: true,
      selectedKeys: [activeKey],
    };
  }, [
    getPersonOptionRow,
    handleChangeFirstLastNameRequirements,
    intl,
    isFirstNameRequired,
    isLastNameRequired,
  ]);

  const getTooltipChildren = useCallback((trigger: HTMLElement) => trigger.parentElement!, []);

  const isDivider = row.fieldType === SchemaFieldType.DIVIDER;

  const isRequiredDisabled = isDivider || isSystemRequired || isCalculated;
  const isRequired = row.required || isSystemRequired;

  return (
    <>
      <div>
        {row.field === PersonFieldName.FIRST_NAME ? (
          <Dropdown menu={personNameMenu} placement="bottomLeft" trigger={["click"]}>
            <Button
              icon={
                <Badge dot={isFirstNameRequired || isLastNameRequired}>
                  <FontAwesomeIcon icon={faAsterisk} />
                </Badge>
              }
              iconOnly
              tabIndex={-1}
            />
          </Dropdown>
        ) : (
          <Tooltip
            destroyTooltipOnHide
            getPopupContainer={getTooltipChildren}
            overlayClassName={styles.tooltip}
            placement="top"
            title={
              isRequiredDisabled
                ? null
                : row.required
                ? intl.formatMessage(messages.dontRequire)
                : intl.formatMessage(messages.markRequired)
            }
            trigger={["hover"]}
          >
            <Button
              disabled={isRequiredDisabled}
              icon={
                <Badge dot={isRequired}>
                  <FontAwesomeIcon icon={faAsterisk} />
                </Badge>
              }
              iconOnly
              onClick={isRequiredDisabled ? undefined : handleToggleRequired}
              tabIndex={-1}
            />
          </Tooltip>
        )}
      </div>
      {contextHolder}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    activityEmailVariantLayout: isEditingActivityEmailVariantLayout(state),
    activityPrimaryLayout: isEditingActivityPrimaryLayout(state),
    entityType: getActiveEntityType(state),
  };
};

export default connect(mapStateToProps)(Requirement);
